import { pipe } from '@alfa-bank/site-invest-common/helpers/utils/pipe';

import type {
    IAsset,
    TConfigUrlToAssetTemplatePage,
    TProfitabilityType,
    IProfitability,
    TAssetTypeCode,
} from '../models';
import { mapDefaultProfitabilityPeriod } from '../config';
import { PROFITABILITY_TYPE_YTM } from '../constants';

import { getFilterType, isNumber, isBond } from './common';

/** Округляет значение до указанной позиции */
export const roundUpValue = (
    value?: number | null,
    toFixed = 0,
    defaultValueToReturn: number | null = 0
) => {
    if (isNumber(value)) {
        const absoluteNumber = Math.abs(value);

        if (absoluteNumber > 1) {
            return Number(value?.toFixed(toFixed));
        } else if (absoluteNumber > 0 && absoluteNumber < 1) {
            // Вычисляем максимальное количество знаков после запятой
            const decimalPlaces = Math.ceil(Math.log10(1 / absoluteNumber));

            // Округляем до максимального разряда
            return Number(value.toFixed(decimalPlaces));
        } else if (absoluteNumber === 0) {
            return 0;
        }
    }

    return defaultValueToReturn;
};
/** Форматирует доходность для вывода на фронте */
export const formatProfitability = <T extends IAsset>(asset: T): T => ({
    ...asset,
    lastPrice: roundUpValue(asset.lastPrice, 2, null),
    profitabilityPercent: roundUpValue(asset.profitabilityPercent, 2, null),
});

/** Формирует ссылку на страничку инструмента */
export const createLinkForPage = <T extends IAsset>(
    url: TConfigUrlToAssetTemplatePage,
    asset: T
) => {
    const { assetType } = asset;

    const field = getFilterType(assetType.code);
    const uniqueId = asset[field];

    return `${url[assetType.code]}${uniqueId}/`;
};
/** Генерирует и добавляет к ассету ссылку на его детальную карточку */
export const generateLinkPageForAssetDetailCard =
    <T extends IAsset>(url: TConfigUrlToAssetTemplatePage) =>
    (asset: T): T => ({ ...asset, linkPage: createLinkForPage(url, asset) });

/** Возвращает период, за который нужно получить доходность */
export const getProfitabilityPeriodType = (
    assetCodeType: TAssetTypeCode,
    profitabilityType?: TProfitabilityType
) => {
    // У облигаций всегда только свой тип доходности
    if (profitabilityType && isBond(assetCodeType)) {
        return mapDefaultProfitabilityPeriod.BOND;
    }

    if (profitabilityType) {
        return profitabilityType;
    }

    return mapDefaultProfitabilityPeriod[assetCodeType];
};

/** Возвращает данные по доходности за переданный период */
export const getProfitabilityByPeriod = (
    profitabilityType: TProfitabilityType,
    profitabilityList: IProfitability[] | null
): IProfitability =>
    profitabilityList?.find(({ type }) => type === profitabilityType) ?? {
        type: profitabilityType,
        profitability: null,
        profitabilityPercent: null,
    };
/**
 * Берет данные по доходности за период и записывает их в поля ассета
 * Сделано для обратной совместимости, чтобы во всех местах, где эти данные будут использоваться, ничего не менять
 */
export const setAssetProfitabilityTypeData =
    <T extends IAsset>(profitabilityType: TProfitabilityType) =>
    (asset: T) => {
        const { assetType, profitabilities } = asset;
        if (!profitabilities?.length) {
            return asset;
        }

        if (isBond(assetType.code)) {
            const { profitability, profitabilityPercent } = getProfitabilityByPeriod(
                PROFITABILITY_TYPE_YTM,
                profitabilities
            );

            return { ...asset, profitability, profitabilityPercent };
        }

        const { profitability, profitabilityPercent } = getProfitabilityByPeriod(
            profitabilityType,
            profitabilities
        );

        return { ...asset, profitability, profitabilityPercent };
    };

// TODO: ввести на следующих итерациях, когда все эмитенты будут готовы передавать данные по типу доходности
// interface IUpdateAssetDataArgs {
//     profitabilityType: TProfitabilityType;
//     url: TConfigUrlToAssetTemplatePage;
// }

/** Функция пайп для подготовки данных на стадии получения ответа от бэка */
export const updateAssetData =
    <T extends IAsset>(
        url: TConfigUrlToAssetTemplatePage,
        profitabilityType?: TProfitabilityType
    ) =>
    (asset: T) => {
        const { assetType } = asset;
        const profitabilityTypeValue = getProfitabilityPeriodType(
            assetType.code,
            profitabilityType
        );

        return pipe<T>(
            // Добавляем к ассету поле linkPage (ссылка на детальную карточку)
            generateLinkPageForAssetDetailCard<T>(url),
            // Исходя из периода записываем доходность в поля profitability и profitabilityPercent
            setAssetProfitabilityTypeData(profitabilityTypeValue),
            // Форматируем значения из profitability и profitabilityPercent до 2-х знаков после запятой
            formatProfitability
        )(asset);
    };
